import { useCallback, useEffect, useState } from "react";

import "@/components/Daily/daily.css";
import "./admin.css";
import { formatPuzzleDate } from "@/daily/game";
import {
  chooseLocalKnowledge,
  fetchUpcomingPuzzles,
  rewriteClues,
  UpcomingPuzzle,
} from "@/services/daily";

// "in 7h 24m", for the time until a puzzle's hint can no longer be changed.
const timeUntil = (iso: string): string => {
  const minutes = Math.max(0, Math.round((Date.parse(iso) - Date.now()) / 60000));
  const hours = Math.floor(minutes / 60);
  return hours > 0 ? `${hours}h ${minutes % 60}m` : `${minutes}m`;
};

interface PuzzleCardProps {
  puzzle: UpcomingPuzzle;
  adminKey: string;
  onChoose: (choice: number) => void;
}

// How long "Confirm rewrite" waits for the second press (ms).
const CONFIRM_FOR = 5000;

const PuzzleCard = ({ puzzle, adminKey, onChoose }: PuzzleCardProps) => {
  const chosen = puzzle.localKnowledgeChoice;

  // Rewriting replaces the candidates below and clears the choice, so it
  // takes two presses.
  const [rewrite, setRewrite] = useState<"idle" | "confirming" | "started" | "failed">("idle");

  useEffect(() => {
    if (rewrite !== "confirming") return;
    const timer = window.setTimeout(() => setRewrite("idle"), CONFIRM_FOR);
    return () => window.clearTimeout(timer);
  }, [rewrite]);

  const onRewrite = () => {
    if (rewrite !== "confirming") return setRewrite("confirming");
    setRewrite("started");
    rewriteClues(adminKey, puzzle.date).catch(() => setRewrite("failed"));
  };

  return (
    <section className="ds-admin__puzzle">
      <header className="ds-admin__header">
        <div>
          <span className="ds-eyebrow ds-eyebrow--red">
            {formatPuzzleDate(puzzle.date, "en")} · {puzzle.flightNumber} · {puzzle.aircraft}
          </span>
          <h2 className="ds-admin__route">
            {puzzle.origin.city} to {puzzle.destination.city}
          </h2>
          <span className="ds-admin__airports">
            {puzzle.origin.iata} {puzzle.origin.name} → {puzzle.destination.iata}{" "}
            {puzzle.destination.name}
          </span>
        </div>
        <div className="ds-admin__actions">
          <a
            className="ds-button ds-button--navy ds-admin__play"
            href={`/daily?preview=${puzzle.date}&key=${encodeURIComponent(adminKey)}`}
            target="_blank"
            rel="noreferrer"
          >
            Play it
          </a>
          <button
            type="button"
            className="ds-button ds-admin__rewrite"
            disabled={rewrite === "started"}
            onClick={onRewrite}
          >
            {rewrite === "confirming"
              ? "Confirm rewrite"
              : rewrite === "started"
                ? "Rewriting…"
                : "Rewrite clues"}
          </button>
        </div>
      </header>

      {rewrite === "confirming" && puzzle.locked && (
        <p className="ds-admin__note">
          This puzzle is live. Its clues will change for everyone, and the best new local-knowledge
          clue will be used straight away.
        </p>
      )}

      {rewrite === "started" && (
        <p className="ds-admin__note">
          Writing new clues for this flight. It takes a minute or two; reload to see them.
        </p>
      )}
      {rewrite === "failed" && (
        <p className="ds-admin__note">The rewrite could not be started. Try again.</p>
      )}

      <ol className="ds-admin__clues">
        {puzzle.clues.map((clue) => (
          <li key={clue.type}>
            <strong>{clue.shortFact}</strong>
            <span>{clue.detail}</span>
          </li>
        ))}
      </ol>

      <div className="ds-admin__hints">
        <span className="ds-label">
          Local knowledge ·{" "}
          {puzzle.locked
            ? "live; choosing another changes it for everyone"
            : chosen === null
            ? `choose one; the first is used in ${timeUntil(puzzle.locksAt)}`
            : `chosen; can be changed for ${timeUntil(puzzle.locksAt)}`}
        </span>
        {puzzle.localKnowledgeOptions.map((option, index) => (
          <button
            key={index}
            type="button"
            className={`ds-admin__hint ${chosen === index ? "ds-admin__hint--chosen" : ""}`}
            aria-pressed={chosen === index}
            onClick={() => onChoose(index)}
          >
            <span className="ds-admin__hint-fact">{option.shortFact}</span>
            {option.quality && (
              <span className="ds-admin__hint-score">
                {option.quality.pointsTo === "letter"
                  ? "Last resort"
                  : `${option.quality.score} in 100 · points to the ${option.quality.pointsTo}`}
              </span>
            )}
            <span className="ds-admin__hint-title">{option.title}</span>
            <span className="ds-admin__hint-detail">{option.detail}</span>
          </button>
        ))}
      </div>
    </section>
  );
};

// The editor's page: the puzzles coming up, each with its candidate
// local-knowledge clues to choose between. Opened with /daily/admin?key=KEY.
const DailyAdmin = () => {
  const [adminKey] = useState(
    () => new URLSearchParams(window.location.search).get("key") ?? ""
  );
  const [puzzles, setPuzzles] = useState<UpcomingPuzzle[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    document.title = "Daily Service - Editor";
    fetchUpcomingPuzzles(adminKey)
      .then(setPuzzles)
      .catch(() => setFailed(true));
  }, [adminKey]);

  const choose = useCallback(
    (date: string, choice: number) => {
      chooseLocalKnowledge(adminKey, date, choice)
        .then((updated) =>
          setPuzzles((current) =>
            (current ?? []).map((puzzle) => (puzzle.date === date ? updated : puzzle))
          )
        )
        .catch(() => setFailed(true));
    },
    [adminKey]
  );

  return (
    <div className="ds ds-admin">
      <h1 className="ds-admin__title">Daily Service · Coming up</h1>
      {failed && <p className="ds-admin__note">That did not work. Check the key in the address.</p>}
      {!failed && puzzles === null && <p className="ds-admin__note">Loading…</p>}
      {puzzles?.length === 0 && <p className="ds-admin__note">No built puzzles yet.</p>}
      {puzzles?.map((puzzle) => (
        <PuzzleCard
          key={puzzle.date}
          puzzle={puzzle}
          adminKey={adminKey}
          onChoose={(choice) => choose(puzzle.date, choice)}
        />
      ))}
    </div>
  );
};

export default DailyAdmin;
